import React, { useState, useEffect } from "react";
import "../../css/TeamWorkspaceMainPage.css";
import "../../css/DocumentationHomeComponent.css";
import "../../css/HomePage.css";
import { Link, useNavigate } from "react-router-dom";
import { auth } from "../../firebase.js";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";

function TeamWorkspaceMainPage() {
    const [loading, setLoading] = useState(true);
    const [ownedWorkspaces, setOwnedWorkspaces] = useState([]);
    const [sharedWorkspaces, setSharedWorkspaces] = useState([]);
    const [userUid, setUserUid] = useState(null);
    const [authenticated, setAuthenticated] = useState(true);
    const { enhancedRightClickEnabled } = useQuickLinks();
    const navigate = useNavigate();

    const loggedOutOptions = [
        { label: "Login", icon: <i className="fas fa-sign-in-alt"></i>, url: "/login", onClick: () => navigate("/login") },
        { label: "Register", icon: <i className="fas fa-user-plus"></i>, url: "/register", onClick: () => navigate("/register") },
        { label: "Try My Notebook", icon: <i className="fas fa-book"></i>, url: "/my-notebook", onClick: () => navigate("/my-notebook") },
    ];

    const loggedInOptions = [
        { label: "Home", icon: <i className="fas fa-home"></i>, url: "/", onClick: () => navigate("/") },
        { label: "Your Documents", url: "/documentation", onClick: () => navigate("/documentation") },
        { label: "Your Walkthroughs", url: "/walkthroughs", onClick: () => navigate("/walkthroughs") },
        { label: "Your Notebook", url: "/my-notebook", onClick: () => navigate("/my-notebook") },
        { label: "Account Settings", url: `/account/${userUid}`, onClick: () => navigate(`/account/${userUid}`) },
    ];

    const menuOptions = userUid ? loggedInOptions : loggedOutOptions;
    const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (!user) {
                setAuthenticated(false);
                setLoading(false);
                return;
            }
            setUserUid(user.uid);
            try {
                const token = await user.getIdToken();
                const response = await fetch("https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/", {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Auth-Token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "Requesting-User-Uid": user.uid,
                    },
                });
                if (!response.ok) throw new Error("Failed to fetch workspaces");
                const workspaces = await response.json();
                setOwnedWorkspaces(workspaces.filter(w => w.role === "OWNER"));
                setSharedWorkspaces(workspaces.filter(w => w.role !== "OWNER"));
            } catch (error) {
                console.error("Error fetching workspaces:", error);
            } finally {
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    if (loading) {
        return (
            <div className="DocumentationHomeComponent__coffee-loader-div">
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p style={{ color: "#5B92E5", fontSize: "1.1rem", marginTop: 16 }}>Loading workspaces...</p>
            </div>
        );
    }

    if (!authenticated) {
        return (
            <div className="DocumentationMainPage__div">
                <div className="DocumentationMainPage__main-content">
                    <p className="large">It looks like you're not signed in.</p>
                    <p className="medium">
                        <strong>To view your Team Workspaces, you must be signed in.</strong>
                    </p>
                    <button onClick={() => navigate("/")} style={{ marginRight: "10px" }} className="ConflictResolver__button">Home</button>
                    <button onClick={() => navigate("/login")} style={{ marginRight: "10px" }} className="ConflictResolver__button">Login</button>
                    <button onClick={() => navigate("/register")} className="ConflictResolver__button">Register</button>
                </div>
            </div>
        );
    }

    if (ownedWorkspaces.length === 0 && sharedWorkspaces.length === 0) {
        return (
            <div className="no-workspace-content TeamWorkspaceMainPage__bg">
                <h1>Create a <span>Workspace</span> For Your <span>Team</span> <i className="fa-regular fa-face-smile-beam"></i></h1>
                <ul>
                    <li>Keep all your team members in one collaborative space</li>
                    <li>Organize team specific documentation</li>
                    <li>Manage team member permissions &amp; roles</li>
                    <li>Easily on-board new team members with team specific walkthroughs</li>
                </ul>
                <Link to="/workspaces/create"><button>Create Workspace</button></Link>
            </div>
        );
    }

    const roleLabel = (role) => {
        const map = { OWNER: "Owner", ADMIN: "Admin", EDITOR: "Editor", VIEWER: "Viewer" };
        return map[role] || role;
    };

    return (
        <div
            className="WorkspaceList__page"
            onContextMenu={
                enhancedRightClickEnabled
                    ? (e) => { e.preventDefault(); showContextMenu(e.clientX, e.clientY); }
                    : undefined
            }
        >
            <div className="WorkspaceList__topbar">
                <button className="WorkspaceList__back-btn" onClick={() => navigate("/")}>
                    <i className="fas fa-arrow-left"></i> Home
                </button>
            </div>

            <div className="WorkspaceList__container">
                <div className="WorkspaceList__header">
                    <div>
                        <h1 className="WorkspaceList__title">My Workspaces</h1>
                        <p className="WorkspaceList__subtitle">Manage your team workspaces or create a new one.</p>
                    </div>
                    <button className="WorkspaceList__create-btn" onClick={() => navigate("/workspaces/create")}>
                        <i className="fas fa-plus"></i> Create New Workspace
                    </button>
                </div>

                {ownedWorkspaces.length > 0 && (
                    <section className="WorkspaceList__section">
                        <h2 className="WorkspaceList__section-title">
                            <i className="fas fa-crown"></i> Workspaces You Own
                        </h2>
                        <div className="WorkspaceList__grid">
                            {ownedWorkspaces.map(workspace => (
                                <div key={workspace.id} className="WorkspaceList__card">
                                    <div className="WorkspaceList__card-body">
                                        <h3 className="WorkspaceList__card-name">{workspace.name}</h3>
                                        {workspace.description && (
                                            <p className="WorkspaceList__card-description">{workspace.description}</p>
                                        )}
                                        <div className="WorkspaceList__card-meta">
                                            <span className="WorkspaceList__badge WorkspaceList__badge--domain">
                                                <i className="fas fa-globe"></i> {workspace.custom_domain}
                                            </span>
                                            <span className="WorkspaceList__badge WorkspaceList__badge--owner">
                                                Owner
                                            </span>
                                            {workspace.theme && workspace.theme !== "Default" && (
                                                <span className="WorkspaceList__badge WorkspaceList__badge--theme">
                                                    {workspace.theme}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="WorkspaceList__card-actions">
                                        <button
                                            className="WorkspaceList__open-btn"
                                            onClick={() => navigate(`/workspace/${workspace.custom_domain}`)}
                                        >
                                            Open <i className="fas fa-arrow-right"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {sharedWorkspaces.length > 0 && (
                    <section className="WorkspaceList__section">
                        <h2 className="WorkspaceList__section-title">
                            <i className="fas fa-users"></i> Shared With You
                        </h2>
                        <div className="WorkspaceList__grid">
                            {sharedWorkspaces.map(workspace => (
                                <div key={workspace.id} className="WorkspaceList__card">
                                    <div className="WorkspaceList__card-body">
                                        <h3 className="WorkspaceList__card-name">{workspace.name}</h3>
                                        {workspace.description && (
                                            <p className="WorkspaceList__card-description">{workspace.description}</p>
                                        )}
                                        <div className="WorkspaceList__card-meta">
                                            <span className="WorkspaceList__badge WorkspaceList__badge--domain">
                                                <i className="fas fa-globe"></i> {workspace.custom_domain}
                                            </span>
                                            <span className="WorkspaceList__badge WorkspaceList__badge--role">
                                                {roleLabel(workspace.role)}
                                            </span>
                                            <span className="WorkspaceList__badge WorkspaceList__badge--owner-info">
                                                <i className="fas fa-user"></i> {workspace.owner}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="WorkspaceList__card-actions">
                                        <button
                                            className="WorkspaceList__open-btn"
                                            onClick={() => navigate(`/workspace/${workspace.custom_domain}`)}
                                        >
                                            Open <i className="fas fa-arrow-right"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>

            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default TeamWorkspaceMainPage;
